import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { LocaleCode, OperationActivityPage } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useOperationActivityQuery,
    useOperationCatalogQuery,
} from '@/api/queries';
import {
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canReadOperations,
    canReverseOperations,
    deriveOperationsAccess,
    type OperationsAccess,
} from '../operationsAccess';
import {
    initialFilters,
    operationFilterOptions,
    toActivityFilters,
    toRowModels,
    type ActivityFilterState,
    type ActivityRowModel,
} from './activityMeta';

export type UseActivityPageResult = {
    access: OperationsAccess;
    canRead: boolean;
    /** Whether the Reverse action is offered at all. The server decides again. */
    canReverse: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    filters: ActivityFilterState;
    onRangeChange: (from: string, to: string) => void;
    onKeyChange: (key: string) => void;
    onStatusChange: (status: string) => void;
    onPageChange: (page: number) => void;
    /** Template keys and their names, for the operation filter. */
    operations: { key: string; label: string }[];
    rows: ActivityRowModel[];
    pageInfo: Pick<
        OperationActivityPage,
        'page' | 'pageSize' | 'totalCount' | 'totalPages'
    >;
    /** The record whose drawer is open, or `null`. */
    selectedRecordId: string | null;
    onSelect: (recordId: string) => void;
    onCloseDetail: () => void;
};

const EMPTY_PAGE = { page: 1, pageSize: 0, totalCount: 0, totalPages: 0 };

/**
 * Drives the Activity tab.
 *
 * Two reads: the register itself, and the catalog behind the filter select.
 *
 * **No write.** Reversal happens in the detail drawer, against the journal, and
 * the drawer owns it — this hook never learns that it happened beyond the
 * refetch the mutation's invalidation triggers.
 */
export const useActivityPage = (): UseActivityPageResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    const access = React.useMemo(
        () => deriveOperationsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadOperations(access);
    const canReverse = canReverseOperations(access);

    // `?key=` pre-filters the register — the link a History version card
    // carries. Read **once, as the initial state**, and then owned by this hook:
    // the URL seeds the screen, it does not drive it, so changing the filter
    // afterwards does not have to rewrite the address bar and the back button
    // does not step through filter changes.
    const [searchParams] = useSearchParams();
    const [filters, setFilters] = React.useState<ActivityFilterState>(() => ({
        ...initialFilters(),
        key: searchParams.get('key') || 'ALL',
    }));
    const [selectedRecordId, setSelectedRecordId] = React.useState<
        string | null
    >(null);

    const requestFilters = React.useMemo(
        () => toActivityFilters(filters),
        [filters],
    );

    const activityQuery = useOperationActivityQuery(requestFilters, canRead);
    const { data: activity, error } = unwrapStatement(
        activityQuery.data,
        activityQuery.error,
    );

    // The catalog is read only to name the operations in the filter select: the
    // activity rows carry their own names, but the filter has to offer every
    // operation, including ones nothing has been recorded against yet.
    const catalogQuery = useOperationCatalogQuery(canRead);
    const catalogData = catalogQuery.data;
    const operations = React.useMemo(
        () =>
            operationFilterOptions(
                catalogData?.success ? catalogData.response.rows : [],
                locale,
            ),
        [catalogData, locale],
    );

    /**
     * Any filter change resets to page 1.
     *
     * Otherwise narrowing a five-page register while sitting on page 4 lands on
     * an empty page that reads as "nothing matched" — the confident wrong answer
     * this folder exists to avoid.
     */
    const setFilter = React.useCallback(
        (patch: Partial<ActivityFilterState>) => {
            setFilters((current) => ({ ...current, ...patch, page: 1 }));
        },
        [],
    );

    const onRangeChange = React.useCallback(
        (from: string, to: string) => setFilter({ from, to }),
        [setFilter],
    );

    const onKeyChange = React.useCallback(
        (key: string) => setFilter({ key }),
        [setFilter],
    );

    const onStatusChange = React.useCallback(
        (status: string) =>
            setFilter({ status: status as ActivityFilterState['status'] }),
        [setFilter],
    );

    const onPageChange = React.useCallback((page: number) => {
        setFilters((current) => ({ ...current, page }));
    }, []);

    const onRetry = React.useCallback(() => {
        activityQuery.refetch();
    }, [activityQuery]);

    return {
        access,
        canRead,
        canReverse,
        locale,
        // `keepPreviousData` holds the previous page on screen while the next
        // one loads, so this is only true on the very first read — which is
        // exactly when the table should skeleton its rows.
        isLoading: canRead && activityQuery.isLoading,
        error,
        onRetry,
        filters,
        onRangeChange,
        onKeyChange,
        onStatusChange,
        onPageChange,
        operations,
        rows: React.useMemo(
            () => toRowModels(activity?.rows ?? []),
            [activity],
        ),
        pageInfo: activity
            ? {
                  page: activity.page,
                  pageSize: activity.pageSize,
                  totalCount: activity.totalCount,
                  totalPages: activity.totalPages,
              }
            : EMPTY_PAGE,
        selectedRecordId,
        onSelect: setSelectedRecordId,
        onCloseDetail: React.useCallback(() => setSelectedRecordId(null), []),
    };
};
